import type { PermissionCatalog, RiskClass } from "@closer/shared";
import { PERMISSION_DOMAIN_NAMES, RISK_CLASS_NAMES } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ContextActivityModal } from "../../components/ContextActivityModal";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { AiOrgAdviceSection } from "./AiOrgAdviceSection";
import type { RoleFormMode } from "./RoleForm";
import { RoleForm } from "./RoleForm";

/**
 * Roller ve Yetkiler (CLOS-191 `[053]`).
 *
 * ### ⚠ Sayfa permission motorunun KAYNAĞI DEĞİL
 *
 * Kart: *"permission adları, açıklamalar ve risk sınıfları CLOS-83 merkezî katalogdan
 * alınır; UI kendi permission anlamını ÜRETMEZ."* Sayfa katalogu sunucudan çekiyor ve
 * hiçbir açıklamayı kendisi yazmıyor. Yazsaydı katalog değiştiğinde iki metin ayrışır ve
 * kullanıcı yetkinin ne yaptığını yanlış öğrenirdi.
 *
 * ### ⚠ Yetki ile KAYIT ERİŞİMİ ayrı
 *
 * Kart: *"rol permission'ı ve kayıt erişimi UI metni, veri modeli ve API sonuçlarında AYRI
 * gösterilir; rol bir kaydı otomatik görünür yapmaz"* ve *"kullanıcı bazlı permission
 * override veya deny SUNULMAZ."* Sayfa bu ayrımı açıkça söylüyor — söylemeseydi yönetici
 * `contract.view` verdiği kişinin bütün sözleşmeleri göreceğini sanır ve gerçek kayıt
 * erişimi kurallarını (CLOS-91–93) hiç aramazdı.
 *
 * ### Sistem rolleri değişmez
 *
 * Sistem rolünde düzenleme/çoğaltma/arşivleme SUNULMUYOR. Bu bir kolaylık değil, kartın
 * kuralının arayüz karşılığı; gerçek koruma sunucuda ve `roles` tablosunun kısıtlarında.
 */

type Rol = {
  id: string;
  name: string;
  key: string | null;
  isSystem: boolean;
  archivedAt: string | null;
  description?: string | null;
  permissions: string[];
  usage: { memberships: number; invitations: number };
  /**
   * BU ROL YÖNETİŞİM ROLÜ MÜ (CLOS-1208).
   *
   * ⚠ Devir ucu CLOS-1207 ile geldi ve çalışıyordu; kararın SONUCU hiçbir yerde
   * görünmüyordu — CLOS-1204'ün varsayılan akış için kaydettiği kusurun aynısı.
   */
  isGovernance: boolean;
  /** Sıralama bilgisi (§7.2). ⚠ Tier YETKİ VERMEZ; yalnız kimin kimden devralabileceğini sınırlar. */
  authorityTier: number;
  parentRoleId: string | null;
  /**
   * ⚠ AD SUNUCUDAN geliyor, listeden türetilmiyor: ata rol arşivliyse varsayılan listede
   * HİÇ olmayabilir ve ekran o zaman "bilinmeyen rolden devralıyor" derdi.
   */
  parentRoleName: string | null;
  /**
   * KALITILAN YETKİLER — `permissions`'tan AYRI (CLOS-1211 AC-5).
   *
   * ⚠ `permissions` YAPILANDIRILMIŞ kümedir. Karıştırsaydık düzenleme ekranı kalıtılanı
   * "kaydedilebilir seçim" sanır ve kaydettiğinde fiziksel satıra çevirirdi: canlı bağ
   * sessizce kopyaya dönüşür, ata rolde yapılan değişiklik artık yansımazdı.
   */
  inheritedPermissions: string[];
};

type Katalog = PermissionCatalog;

const RISK_TONU: Record<RiskClass, "neutral" | "warning" | "danger"> = {
  standard: "neutral",
  sensitive: "warning",
  protected: "danger",
};

export function RolesPage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();

  const [arsivliler, setArsivliler] = useState(false);
  const [detay, setDetay] = useState<Rol | null>(null);
  const [form, setForm] = useState<RoleFormMode | null>(null);

  const rolYonetebilir = can("organization.role");
  const yonetisimDevredebilir = can("organization.governance");

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "roles-page", String(arsivliler)),
    queryFn: async () =>
      (await api<Rol[]>(`/api/roles?includeArchived=${String(arsivliler)}`, { tenantId })).data,
  });

  /**
   * Katalog AYRI sorgu ve rol listesinden bağımsız: yetki açıklamaları organizasyona değil
   * ÜRÜNE ait. Tek yanıtta birleştirilseydi aynı katalog her rol listesi isteğinde yeniden
   * gönderilirdi.
   */
  const katalog = useQuery({
    queryKey: tenantKey(tenantId, "permission-catalog"),
    queryFn: async () => (await api<Katalog>("/api/roles/catalog", { tenantId })).data,
  });

  /**
   * YÖNETİŞİM DEVRİ (CLOS-1208 — kanon §7.5).
   *
   * ⚠ ONAY ADIMI VAR ve bu bir tercih değil. Kanon devri *"ayrı, high-impact, audit'li
   * command"* diye tanımlıyor: tek tıkla devretseydik kullanıcı çekirdeğin taşındığını ve
   * eski rolün yetkiyi kaybettiğini ancak olduktan SONRA öğrenirdi.
   *
   * ⚠ Kapı `organization.governance`, `organization.role` DEĞİL — sunucudakiyle aynı.
   * Rol düzenleyebilen herkes yönetişimi devredebilseydi kanonun ayırdığı iki karar
   * arayüzde yeniden birleşirdi. Buradaki gizleme bir KOLAYLIK; gerçek kapı arka uçta.
   */
  const [devirHedefi, setDevirHedefi] = useState<Rol | null>(null);

  const devir = useMutation({
    mutationFn: async (rol: Rol) => {
      await api(`/api/roles/${rol.id}/governance`, { method: "PUT", tenantId });
    },
    onSuccess: async () => {
      setDevirHedefi(null);
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "roles-page", String(arsivliler)),
      });
      toast.success("Yönetişim rolü devredildi");
    },
    /**
     * ⚠ SUNUCUNUN CÜMLESİ (AC-4). Kendi metnimizi uydursaydık ("devir başarısız"),
     * kullanıcı ne yapacağını öğrenemezdi; sunucu tam olarak söylüyor: *"önce bu role bir
     * aktif üye atayın"*.
     */
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "Devir tamamlanamadı, tekrar deneyin.",
      ),
  });

  /**
   * ROL HİYERARŞİSİ (CLOS-1211 — kanon §7.2/§7.3).
   *
   * ⚠ İKİ AYRI MUTATION, tek modal. Bağ ile seviye iki farklı karar: seviyeyi değiştirmek
   * isteyen kullanıcı aynı istekte bağı da göndermek zorunda kalsaydı, dokunmadığı bir
   * kararı yeniden onaylamış olurdu.
   */
  const [hiyerarsiRolu, setHiyerarsiRolu] = useState<Rol | null>(null);

  const bagKaydet = useMutation({
    mutationFn: async (istek: { roleId: string; parentRoleId: string | null }) => {
      await api(`/api/roles/${istek.roleId}/parent`, {
        method: "PUT",
        tenantId,
        body: JSON.stringify({ parentRoleId: istek.parentRoleId }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "roles-page", String(arsivliler)),
      });
      toast.success("Yetki devralma bağı güncellendi");
    },
    /** ⚠ SUNUCUNUN cümlesi (AC-2): "bağ kurulamadı" deseydik kullanıcı sebebi öğrenemezdi. */
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "Bağ kaydedilemedi, tekrar deneyin.",
      ),
  });

  const seviyeKaydet = useMutation({
    mutationFn: async (istek: { roleId: string; authorityTier: number }) => {
      await api(`/api/roles/${istek.roleId}/tier`, {
        method: "PUT",
        tenantId,
        body: JSON.stringify({ authorityTier: istek.authorityTier }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "roles-page", String(arsivliler)),
      });
      toast.success("Yetki seviyesi güncellendi");
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "Seviye kaydedilemedi, tekrar deneyin.",
      ),
  });

  const komut = useMutation({
    mutationFn: async (istek: { roleId: string; yol: "archive" | "reactivate" }) => {
      await api(`/api/roles/${istek.roleId}/${istek.yol}`, { method: "POST", tenantId });
      return istek.yol;
    },
    onSuccess: async (yol) => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "roles-page", String(arsivliler)),
      });
      toast.success(yol === "archive" ? "Rol arşivlendi" : "Rol yeniden aktifleştirildi");
    },
    /**
     * ⚠ SUNUCUNUN cümlesi: *"bu rol 1 bekleyen davette kullanılıyor"* gibi bir ret,
     * yöneticiye ne yapacağını söyleyen tek metin.
     */
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      ),
  });

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Roller yükleniyor">
        <Skeleton lines={5} />
      </div>
    );
  }

  if (sorgu.error || !sorgu.data) {
    return (
      <StateView
        state={resolveErrorState(sorgu.error, {
          idempotent: true,
          existenceIsSecret: false,
          overrides: {
            error: {
              title: "Roller yüklenemedi",
              description: "Liste getirilemedi. Lütfen tekrar deneyin.",
            },
          },
        })}
        onRetry={() => void sorgu.refetch()}
      />
    );
  }

  /** ⚠ Mevcut yönetişim rolü listeden TÜRETİLİYOR: ikinci bir sorgu aynı gerçeği iki kez sorardı. */
  const yonetisimRolu = sorgu.data.find((r) => r.isGovernance) ?? null;

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader
        title="Roller ve Yetkiler"
        description="İzin setlerini tanımlayın ve her rolün risk sınıfını görüntüleyin."
      />

      {/*
        AI YETKİ DÜZENİ ÖNERİLERİ (CLOS-288 `[192]`) — rol listesinin ÜSTÜNDE ama
        listeden AYRI bir kart: öneriler rollerin kendisi değil, onlara BAKAN bir okuma.
        Bir "uygula" düğmesi yok; değişiklik aşağıdaki kanonik rol akışında yapılıyor.
      */}
      <AiOrgAdviceSection />

      <Card title="Roller ve Yetkiler">
        <div className="flex flex-col gap-4">
          {/*
            ⚠ Ayrım SÖYLENİYOR. Söylenmeseydi yönetici `contract.view` verdiği kişinin
            bütün sözleşmeleri göreceğini sanır ve kayıt erişimi kurallarını hiç aramazdı.
          */}
          <p className="text-sm text-fg-muted">
            Yetkiler bir kaydı otomatik görünür yapmaz: hangi sözleşmeyi kimin göreceği ayrı bir
            kayıt erişimi katmanında belirlenir.
          </p>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Checkbox
              label="Arşivlenenleri göster"
              checked={arsivliler}
              onChange={(event) => setArsivliler(event.target.checked)}
            />
            {rolYonetebilir ? (
              <Button size="sm" onClick={() => setForm({ kind: "create" })}>
                Yeni rol
              </Button>
            ) : null}
          </div>

          <ul className="flex flex-col divide-y divide-border-default">
            {sorgu.data.map((rol) => (
              <li
                key={rol.id}
                data-testid="rol-satiri"
                className="flex flex-wrap items-center justify-between gap-2 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-fg-strong">{rol.name}</p>
                  <p className="truncate text-sm text-fg-muted">
                    {/* Üyelik ve davet AYRI: tek sayıya toplamak iki farklı işi gizlerdi. */}
                    {rol.usage.memberships} üyelik · {rol.usage.invitations} bekleyen davet
                  </p>
                  {/*
                    ⚠ AC-3: KİMDEN DEVRALDIĞI GÖRÜNÜYOR. CLOS-1210 bağı kurabiliyordu ama
                    hiçbir yerde göstermiyordu; görünmeyen bir kalıtım, "bu rol bunu nereden
                    aldı?" sorusunu yine cevapsız bırakırdı.
                  */}
                  {rol.parentRoleName ? (
                    <p className="truncate text-xs text-fg-soft">
                      {rol.parentRoleName} rolünden yetki devralıyor
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {rol.isSystem ? <Badge>Sistem rolü</Badge> : null}
                  {rol.archivedAt ? <Badge tone="warning">Arşivli</Badge> : null}
                  {/*
                    ⚠ DURUM ROZETİ KOMUTTAN AYRI (CLOS-1204'ün canlıda öğrettiği ders):
                    aşağıdaki devir düğmesi `organization.governance` kapısının ardında,
                    ama yönetişimin KİMDE olduğu herkese ait bir gerçek.
                  */}
                  {rol.isGovernance ? <Badge tone="info">Yönetişim rolü</Badge> : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`${rol.name} yetkileri`}
                    onClick={() => setDetay(rol)}
                  >
                    Yetkiler
                  </Button>
                  {/*
                    ⚠ ROL AKTİVİTESİNİN TÜKETİCİSİ (CLOS-881): `[055]` rol satırlarını
                    üretiyor ve kapısı `organization.view` ile yazılı, ama onları soran
                    hiçbir yüzey yoktu. Kapı burada TEKRARLANMIYOR — sunucu satır satır
                    karar veriyor; ikinci bir istemci kuralı bir gün ondan ayrışırdı.
                  */}
                  <ContextActivityModal entityType="role" entityId={rol.id} baslik={rol.name} />
                  {/*
                    ⚠ Yönetişim rolünün KENDİSİNE devir teklif edilmiyor: sunucu da aynı
                    rolü yeniden atamayı bir DEĞİŞİKLİK saymıyor ve audit yazmıyor.

                    ⚠ Arşivli rolde çizilmiyor çünkü sunucu O YOLDA gerçekten reddediyor
                    ("arşivlenmiş bir role yönetişim devredilemez"). Aktif üye şartı ise
                    burada TEKRARLANMIYOR: listedeki `usage.memberships` sayacı "ended
                    olmayan" üyelikleri sayıyor, sunucunun aradığı ise "active" — iki farklı
                    soru. Kendi kuralımızı uydursaydık sunucunun izin verdiği bir devri
                    yasaklayabilirdik; ret, sunucunun kendi cümlesiyle geliyor.
                  */}
                  {/*
                    ⚠ GELİŞMİŞ: hiyerarşi günlük bir iş değil. Düzenle/Çoğalt ile aynı
                    görsel ağırlıkta dursaydı, "mevcut rolden başla" (kopya) arayan kullanıcı
                    yanlışlıkla canlı bir bağ kurabilirdi — kanon §7.4 ikisini ayırıyor.

                    ⚠ SİSTEM ROLÜNDE ÇİZİLMİYOR ve bu canlıda öğrenildi (sha-48e9110): düğme
                    oradayken seviye kaydedildi, ekran "güncellendi" dedi, DB'de hiçbir şey
                    değişmedi — `roles` üzerindeki RESTRICTIVE RLS politikası
                    (`system_roles_immutable`) UPDATE'i sessizce sıfır satıra indiriyor.
                    Sunucu artık açıkça reddediyor; ekran da kullanıcıyı o reddin üstüne
                    göndermiyor. Sayfanın zaten uyguladığı kuralın aynısı: sistem rolünde
                    yaşam döngüsü komutu yok.

                    ⚠ Sistem rolü ATA olarak hâlâ seçilebiliyor: yasak onun KENDİ düzenine
                    ait, başkasına ata olmasına değil.
                  */}
                  {!rol.isSystem && rolYonetebilir && !rol.archivedAt ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="rol-hiyerarsi"
                      aria-label={`${rol.name} gelişmiş ayarları`}
                      onClick={() => setHiyerarsiRolu(rol)}
                    >
                      Gelişmiş
                    </Button>
                  ) : null}
                  {yonetisimDevredebilir && !rol.isGovernance && !rol.archivedAt ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="yonetisim-devret"
                      onClick={() => setDevirHedefi(rol)}
                    >
                      Yönetişimi devret
                    </Button>
                  ) : null}
                  {/*
                    ⚠ Sistem rolünde HİÇBİR yaşam döngüsü komutu yok (kart: "düzenlenemez,
                    çoğaltılamaz veya arşivlenemez; yalnız güvenli detayları görüntülenir").
                  */}
                  {!rol.isSystem && rolYonetebilir ? (
                    rol.archivedAt ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={komut.isPending}
                        onClick={() => komut.mutate({ roleId: rol.id, yol: "reactivate" })}
                      >
                        Yeniden aktifleştir
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setForm({ kind: "edit", role: rol })}
                        >
                          Düzenle
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setForm({ kind: "duplicate", role: rol })}
                        >
                          Çoğalt
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={komut.isPending}
                          onClick={() => komut.mutate({ roleId: rol.id, yol: "archive" })}
                        >
                          Arşivle
                        </Button>
                      </>
                    )
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      {detay ? (
        <PermissionDetail role={detay} catalog={katalog.data} onClose={() => setDetay(null)} />
      ) : null}

      {form ? (
        <RoleForm
          tenantId={tenantId}
          mode={form}
          catalog={katalog.data}
          onClose={() => setForm(null)}
        />
      ) : null}

      {/*
        DEVİR ONAYI (AC-3) — sonucu ÖNCEDEN söylüyor.

        ⚠ Metin iki sonucu birden anlatıyor çünkü devir iki şey birden yapıyor: hedef rol
        System Governance Core'u devralıyor VE mevcut yönetişim rolü onu kaybediyor.
        Yalnız ilkini söyleseydik, yöneticinin kendi rolünün ne kaybettiğini görmesi için
        devri yapması gerekirdi.

        ⚠ Rol adları modalın kendi cümlesinde geçiyor: "bir role devret" gibi soyut bir
        metin, yanlış satırın düğmesine basıldığını fark ettirmezdi.
      */}
      {hiyerarsiRolu ? (
        <RoleHierarchyModal
          role={hiyerarsiRolu}
          roles={sorgu.data}
          onClose={() => setHiyerarsiRolu(null)}
          onSaveParent={(parentRoleId) =>
            bagKaydet.mutate({ roleId: hiyerarsiRolu.id, parentRoleId })
          }
          onSaveTier={(authorityTier) =>
            seviyeKaydet.mutate({ roleId: hiyerarsiRolu.id, authorityTier })
          }
          bagKaydediliyor={bagKaydet.isPending}
          seviyeKaydediliyor={seviyeKaydet.isPending}
        />
      ) : null}

      {devirHedefi ? (
        <Modal
          open
          onClose={() => setDevirHedefi(null)}
          title="Yönetişim rolünü devret"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDevirHedefi(null)}>
                Vazgeç
              </Button>
              <Button loading={devir.isPending} onClick={() => devir.mutate(devirHedefi)}>
                Devret
              </Button>
            </div>
          }
        >
          <p className="text-sm text-fg" data-testid="yonetisim-devir-sonuc">
            <strong>{devirHedefi.name}</strong> rolü yönetişim rolü olacak ve System Governance Core
            yetkilerini devralacak.
            {yonetisimRolu ? (
              <>
                {" "}
                <strong>{yonetisimRolu.name}</strong> rolü bu yetkileri kaybedecek.
              </>
            ) : null}
          </p>
        </Modal>
      ) : null}
    </div>
  );
}

/**
 * Rolün yetkileri — açıklama ve risk sınıfı KATALOGDAN.
 *
 * ⚠ Katalogda olmayan anahtar HAM hâliyle gösteriliyor: gizlemek, o yetkiyi "yok" gibi
 * göstermek olurdu ve rolün gerçekten neye izin verdiği ekranda eksik kalırdı.
 */
function PermissionDetail({
  role,
  catalog,
  onClose,
}: {
  role: Rol;
  catalog: Katalog | undefined;
  onClose: () => void;
}) {
  const indeks = new Map((catalog?.entries ?? []).map((e) => [e.key, e]));

  return (
    <Modal open onClose={onClose} title={`${role.name} yetkileri`}>
      <div className="flex flex-col gap-3">
        {role.description ? <p className="text-sm text-fg-muted">{role.description}</p> : null}
        <ul className="flex flex-col gap-2">
          {role.permissions.map((anahtar) => {
            const girdi = indeks.get(anahtar);
            return (
              <li key={anahtar} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-fg-strong">{girdi?.description ?? anahtar}</p>
                  <p className="text-xs text-fg-soft">
                    {girdi ? PERMISSION_DOMAIN_NAMES[girdi.domain] : anahtar}
                  </p>
                </div>
                {girdi ? (
                  <Badge tone={RISK_TONU[girdi.risk]}>{RISK_CLASS_NAMES[girdi.risk]}</Badge>
                ) : null}
              </li>
            );
          })}
        </ul>

        {/*
          ⚠ AC-5 — KALITILAN YETKİLER AYRI BÖLÜMDE VE DÜZENLENEMEZ.

          Yapılandırılmış kümeyle aynı listede gösterseydik, kullanıcı onları da bu rolün
          kendi seçimi sanırdı; düzenleme ekranı da aynı yanılgıya düşer ve kaydettiğinde
          canlı bağı sessizce kopyaya çevirirdi (CLOS-1209'un dersi).

          ⚠ Bölüm hiçbir kontrol taşımıyor: burada değiştirilecek şey bu rol değil, ATA rol.
        */}
        {role.inheritedPermissions.length > 0 ? (
          <div className="flex flex-col gap-2 border-t border-border-default pt-3">
            <p className="text-sm font-medium text-fg-strong">
              {role.parentRoleName
                ? `${role.parentRoleName} rolünden devralınan yetkiler`
                : "Devralınan yetkiler"}
            </p>
            <p className="text-xs text-fg-soft">
              Bu yetkiler devralınan rolden gelir ve burada düzenlenemez; değiştirmek için o rolü
              düzenleyin.
            </p>
            <ul className="flex flex-col gap-2" data-testid="kalitilan-yetkiler">
              {role.inheritedPermissions.map((anahtar) => {
                const girdi = indeks.get(anahtar);
                return (
                  <li key={anahtar} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-fg-strong">{girdi?.description ?? anahtar}</p>
                      <p className="text-xs text-fg-soft">
                        {girdi ? PERMISSION_DOMAIN_NAMES[girdi.domain] : anahtar}
                      </p>
                    </div>
                    {girdi ? (
                      <Badge tone={RISK_TONU[girdi.risk]}>{RISK_CLASS_NAMES[girdi.risk]}</Badge>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * GELİŞMİŞ AYARLAR — yetki devralma ve seviye (CLOS-1211, kanon §7.2/§7.3/§7.4).
 *
 * ### ⚠ KOPYA İLE CANLI KALITIM AYRI SEÇİMLER (AC-4)
 *
 * Kanon §7.4 "mevcut rolden başla" varsayılanının **one-time permission copy** olduğunu
 * söylüyor; canlı inheritance AYRI bir seçim. İkisi aynı düğmenin arkasına saklanırsa
 * kullanıcı kopyalamayla kalıtımı karıştırır ve fark ancak ata rol değiştiğinde ortaya
 * çıkar — yani en geç anda. Bu yüzden modal farkı kendi cümlesiyle söylüyor.
 *
 * ### ⚠ İKİ AYRI KAYDET, tek pencere
 *
 * Bağ ile seviye iki farklı karar ve sunucuda iki ayrı uç. Tek "Kaydet" koysaydık,
 * seviyeye dokunan kullanıcı bağı da yeniden göndermiş olurdu.
 *
 * ### ⚠ ADAY LİSTESİ SUNUCUNUN KURALINI TEKRARLAMIYOR
 *
 * Seçenekler tier'a göre süzülmüyor: sunucunun kuralı "ata rolün tier'ı KESİN küçük" ve o
 * karşılaştırma kaydetme anındaki değerlere bakıyor. Burada süzseydik, listedeki bayat bir
 * tier yüzünden sunucunun kabul edeceği bir bağı kullanıcıya yasaklayabilirdik (CLOS-1202
 * dersi). Ret, sunucunun kendi cümlesiyle geliyor.
 */
function RoleHierarchyModal({
  role,
  roles,
  onClose,
  onSaveParent,
  onSaveTier,
  bagKaydediliyor,
  seviyeKaydediliyor,
}: {
  role: Rol;
  roles: Rol[];
  onClose: () => void;
  onSaveParent: (parentRoleId: string | null) => void;
  onSaveTier: (authorityTier: number) => void;
  bagKaydediliyor: boolean;
  seviyeKaydediliyor: boolean;
}) {
  const [ata, setAta] = useState(role.parentRoleId ?? "");
  const [seviye, setSeviye] = useState(String(role.authorityTier));

  /** ⚠ Rolün kendisi aday değil: sunucu da "kendi kendisinden devralamaz" diyor. */
  const adaylar = roles.filter((r) => r.id !== role.id && !r.archivedAt);

  return (
    <Modal open onClose={onClose} title={`${role.name} — gelişmiş ayarlar`}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted" data-testid="kalitim-kopya-farki">
          Çoğalt komutu yetkilerin bir kopyasını çıkarır; buradaki bağ canlıdır: devralınan rolün
          yetkisi değiştiğinde bu rol de anında etkilenir.
        </p>

        <FormField label="Yetki devralınan rol">
          <Select
            data-testid="ata-rol-secimi"
            value={ata}
            onChange={(event) => setAta(event.target.value)}
          >
            {/* ⚠ Boş seçenek bir HATA değil BİR KOMUT: bağı kaldırır. */}
            <option value="">Yok</option>
            {adaylar.map((aday) => (
              <option key={aday.id} value={aday.id}>
                {aday.name}
              </option>
            ))}
          </Select>
        </FormField>
        <div className="flex justify-end">
          <Button
            size="sm"
            loading={bagKaydediliyor}
            onClick={() => onSaveParent(ata === "" ? null : ata)}
          >
            Bağı kaydet
          </Button>
        </div>

        <FormField label="Yetki seviyesi">
          <Input
            type="number"
            data-testid="rol-seviyesi"
            value={seviye}
            onChange={(event) => setSeviye(event.target.value)}
          />
        </FormField>
        {/* ⚠ Seviyenin yetki VERMEDİĞİ söyleniyor: söylenmeseydi yönetici seviyeyi
            yükselterek yetki kazandırdığını sanırdı (kanon §7.2 bunu ayrıca vurguluyor). */}
        <p className="text-xs text-fg-soft">
          Seviye yetki vermez; yalnız hangi rolün hangisinden devralabileceğini sıralar.
        </p>
        <div className="flex justify-end">
          <Button size="sm" loading={seviyeKaydediliyor} onClick={() => onSaveTier(Number(seviye))}>
            Seviyeyi kaydet
          </Button>
        </div>
      </div>
    </Modal>
  );
}
